/* eslint-disable react/no-unescaped-entities */
import { Link } from "react-router-dom";

const Disclaimer = () => (
  <div className="my-7 p-5 rounded-2xl border border-[#bc6c25]/30 bg-[#bc6c25]/5 text-[#1a2421] text-sm leading-relaxed">
    <strong>Aviso médico importante:</strong> este artículo es informativo. No sustituye consulta médica ni tratamiento prescrito. <strong>No suspendas medicamentos</strong> sin hablar antes con tu profesional de salud. Los suplementos descritos son apoyo complementario, no cura ni reemplazo.
  </div>
);

const Callout = ({ children }) => (
  <div className="my-7 p-5 border-l-4 border-[#bc6c25] bg-[#bc6c25]/5 rounded-r-2xl text-[#1a2421] text-[15px] leading-relaxed">
    {children}
  </div>
);

const TOC = ({ items }) => (
  <nav className="my-7 p-5 bg-[#f0ebe1] rounded-2xl border border-[#2c4a3b]/8 not-prose">
    <p className="text-[10px] uppercase tracking-[0.22em] text-[#bc6c25] font-bold">En este artículo</p>
    <ol className="mt-3 space-y-1.5 text-sm text-[#1a2421]">
      {items.map((it, i) => (
        <li key={i} className="flex gap-2">
          <span className="text-[#bc6c25] font-bold">{i + 1}.</span>
          <a href={`#${it.id}`} className="hover:text-[#bc6c25]">{it.label}</a>
        </li>
      ))}
    </ol>
  </nav>
);

const Esperanza = () => (
  <div className="my-9 p-7 rounded-3xl bg-gradient-to-br from-[#1a2421] to-[#2c4a3b] text-[#f9f6f0] not-prose">
    <p className="text-[10px] uppercase tracking-[0.28em] text-[#bc6c25] font-semibold">Eliora Longevity</p>
    <h3 className="font-serif text-2xl md:text-3xl mt-2 leading-tight">Si tienes vida, tienes esperanza.</h3>
    <p className="text-sm text-[#f9f6f0]/75 mt-3 leading-relaxed">
      Hagamos juntas un protocolo personalizado para tu caso. 3 minutos. Gratis. Solo necesitas atreverte a empezar.
    </p>
    <Link to="/#quiz" className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#bc6c25] hover:bg-[#9c581e] text-white text-sm font-semibold">
      Hacer el Quiz Wellness →
    </Link>
  </div>
);

export const ARTICLE_CONTENT_5 = {
  /* 15. Diabetes tipo 2 y glutatión */
  "diabetes-tipo-2-y-glutation": (
    <>
      <p>Si vives con diabetes tipo 2, ya sabes que es una enfermedad que se construye con los años y, una vez instalada, requiere manejo de por vida. Lo que probablemente NO te explicaron en consulta es que la diabetes <strong>destruye literalmente tu sistema antioxidante</strong> —y específicamente tu glutatión, el antioxidante maestro de cada célula—. Esa es una de las razones por las que la diabetes daña tejidos lentamente: ojos, riñones, nervios, corazón.</p>
      <p>La buena noticia: el daño se puede frenar. Y para mucha gente, incluso revertir en grado significativo. Te explico.</p>

      <Disclaimer />

      <TOC items={[
        { id: "que-pasa", label: "Qué pasa cuando hay diabetes" },
        { id: "gsh-bajo", label: "Por qué los diabéticos tienen glutatión bajo" },
        { id: "estudios", label: "Lo que dicen los estudios" },
        { id: "soporte", label: "Soporte celular con Immunocal" },
        { id: "protocolo", label: "Protocolo integral (sin sustituir medicación)" },
      ]} />

      <h2 id="que-pasa">Qué pasa cuando hay diabetes tipo 2</h2>
      <p>La diabetes tipo 2 es una <strong>enfermedad de resistencia a la insulina</strong>. Las células dejan de "escuchar" bien a la insulina, la glucosa se queda en sangre y el cuerpo entra en un estado de glucotoxicidad y estrés oxidativo crónico. Cada molécula de glucosa elevada genera radicales libres. Multiplicado por años → daño acumulativo.</p>
      <p>Por eso las complicaciones clásicas (retinopatía, nefropatía, neuropatía, riesgo cardiovascular) son <strong>todas</strong> consecuencia del estrés oxidativo sostenido en tejidos sensibles.</p>

      <h2 id="gsh-bajo">Por qué los diabéticos tienen glutatión bajo</h2>
      <p>Estudios consistentes muestran que personas con diabetes tipo 2 tienen <strong>niveles de glutatión intracelular un 40-50 % más bajos</strong> que personas sanas de la misma edad. El cuerpo está usando glutatión para neutralizar el daño constantemente, y lo gasta más rápido de lo que puede reponer.</p>

      <Callout>
        <strong>Dato concreto:</strong> un estudio en <em>Diabetes Care</em> mostró que suplementar con precursores de cisteína (el aminoácido del que tu cuerpo fabrica glutatión) durante 14 días en pacientes diabéticos restauró niveles de glutatión a rangos casi normales.
      </Callout>

      <h2 id="estudios">Lo que dicen los estudios</h2>
      <p>Hay literatura que conecta directamente glutatión y diabetes:</p>
      <ul>
        <li><strong>Sekhar et al. (2011)</strong>: pacientes con diabetes tipo 2 mejoraron su perfil oxidativo significativamente con precursores de glutatión.</li>
        <li><strong>Bashar et al. (2018)</strong>: la suplementación con cisteína mejoró sensibilidad a insulina en estudios humanos.</li>
        <li>Datos clínicos sostenidos asocian glutatión elevado con menor riesgo de complicaciones microvasculares.</li>
      </ul>

      <h2 id="soporte">Soporte celular con Immunocal</h2>
      <p><Link to="/immunocal">Immunocal</Link> aporta cisteína bioactiva no desnaturalizada, el precursor más estudiado para elevar glutatión intracelular. NO baja directamente la glucosa, pero <strong>reduce el daño que la glucosa elevada hace a tus tejidos</strong>, lo que importa tanto o más que el número en el glucómetro a largo plazo.</p>

      <h2 id="protocolo">Protocolo integral (sin sustituir medicación)</h2>
      <ol>
        <li><strong>No suspendas metformina, insulina ni medicamentos prescritos.</strong> Habla con tu médica antes de cualquier ajuste.</li>
        <li><strong>Immunocal 1-2 sobres/día</strong> en agua fría, en ayunas. Sostener 90+ días.</li>
        <li><strong>Omega-3 EPA/DHA &gt;2 g/día</strong>: reduce inflamación crónica que acompaña a la diabetes. Ver <Link to="/blog/omega-gen-v-5-en-1">Omega Gen V</Link>.</li>
        <li><strong>Caminar 15 min después de cada comida.</strong> Reduce el pico glucémico hasta 30 %.</li>
        <li><strong>Eliminar harinas refinadas, refrescos, ultraprocesados.</strong></li>
        <li><strong>Entrenamiento de fuerza 2-3 veces/semana.</strong> Aumenta sensibilidad a insulina.</li>
        <li><strong>Sueño 7-9 h consistente.</strong> Una mala noche eleva glucemia al día siguiente.</li>
      </ol>

      <Esperanza />

      <p>Para entender la conexión con el estrés oxidativo, lee <Link to="/blog/estres-oxidativo-explicado">esta guía</Link>. Si quieres un protocolo personalizado, <Link to="/#quiz">haz el quiz wellness gratis</Link>.</p>
    </>
  ),

  /* 16. Hipertensión */
  "hipertension-y-antioxidantes": (
    <>
      <p>"Tu presión está alta. Vas a tomar esto el resto de tu vida." Si esa frase te suena familiar, sabes el peso emocional que carga. Pero hay algo que casi ningún cardiólogo te explica: la hipertensión esencial está profundamente conectada con el <strong>estrés oxidativo del endotelio</strong> (la capa interna de tus arterias). Y eso, en buena medida, se puede apoyar bioquímicamente.</p>

      <Disclaimer />

      <TOC items={[
        { id: "que-pasa", label: "Qué le pasa a tus arterias" },
        { id: "rol-gsh", label: "El rol del glutatión y óxido nítrico" },
        { id: "alimentos", label: "Alimentos que dilatan vasos" },
        { id: "suplementos", label: "Suplementos con evidencia" },
        { id: "protocolo", label: "Protocolo integral" },
      ]} />

      <h2 id="que-pasa">Qué le pasa a tus arterias</h2>
      <p>Tus arterias son tubos vivos, dinámicos. Su capa interna (endotelio) produce constantemente <strong>óxido nítrico (NO)</strong>, una molécula que las relaja y permite que la sangre fluya con presión normal. Cuando hay estrés oxidativo crónico, el óxido nítrico se "apaga" porque reacciona con radicales libres antes de poder hacer su función. Resultado: arterias contraídas → presión arterial elevada.</p>

      <h2 id="rol-gsh">El rol del glutatión y óxido nítrico</h2>
      <p>El glutatión <strong>protege el óxido nítrico</strong> del daño oxidativo. Más glutatión = más NO disponible = arterias más relajadas. Estudios muestran que personas con hipertensión tienen niveles significativamente más bajos de glutatión que personas con presión normal.</p>

      <Callout>
        Una investigación publicada en <em>Hypertension Research</em> mostró que aumentar precursores de glutatión durante 8 semanas redujo presión sistólica entre 4-9 mmHg en pacientes hipertensos. No reemplaza tratamiento, pero es ayuda real.
      </Callout>

      <h2 id="alimentos">Alimentos que dilatan vasos</h2>
      <ul>
        <li><strong>Remolacha (betabel):</strong> 250 ml de jugo diario baja presión 4-8 mmHg.</li>
        <li><strong>Ajo crudo</strong> (1-2 dientes).</li>
        <li><strong>Verduras de hoja verde</strong> (nitratos naturales).</li>
        <li><strong>Frutos rojos</strong> (polifenoles).</li>
        <li><strong>Cacao puro &gt;80 %</strong>.</li>
        <li><strong>Pescado azul</strong> (omega-3).</li>
      </ul>

      <h2 id="suplementos">Suplementos con evidencia</h2>
      <ol>
        <li><Link to="/immunocal">Immunocal</Link>: precursor de glutatión.</li>
        <li>Omega-3 EPA/DHA &gt;2 g/día (<Link to="/blog/omega-gen-v-5-en-1">Omega Gen V</Link>).</li>
        <li>Magnesio glicinato 200-400 mg.</li>
        <li>CoQ10 100-200 mg (especialmente si tomas estatinas).</li>
        <li>Vitamina D3 + K2 con análisis previo.</li>
      </ol>

      <h2 id="protocolo">Protocolo integral</h2>
      <ol>
        <li><strong>No suspendas antihipertensivos sin orden médica.</strong></li>
        <li>Reduce sodio procesado (no la sal natural — el problema es el sodio oculto en ultraprocesados).</li>
        <li>Camina 30 min al día.</li>
        <li>Reduce alcohol al mínimo.</li>
        <li>Maneja estrés diario (respiración 4-7-8, oración, naturaleza).</li>
        <li>Suplementación dirigida (arriba).</li>
        <li>Reevalúa con tu médica cada 3 meses.</li>
      </ol>

      <Esperanza />
    </>
  ),

  /* 17. Tiroides */
  "tiroides-hipotiroidismo-hashimoto": (
    <>
      <p>La tiroides es una glándula pequeña, pero gobierna un imperio. Regula metabolismo, temperatura, energía, ánimo, peso, cabello, ciclos hormonales. Cuando se desregula —y especialmente en <strong>tiroiditis de Hashimoto</strong>, la causa autoinmune más frecuente— el cuerpo se siente apagado, frío, lento.</p>
      <p>Lo que poca gente sabe: la tiroiditis de Hashimoto es <strong>autoinmunidad</strong>, y la autoinmunidad está profundamente conectada con el estrés oxidativo y la inflamación. El glutatión tiene un papel central.</p>

      <Disclaimer />

      <TOC items={[
        { id: "hashimoto", label: "Qué es Hashimoto" },
        { id: "gsh-tiroides", label: "Glutatión y tiroides" },
        { id: "selenio", label: "Selenio: el mineral indispensable" },
        { id: "alimentos", label: "Alimentos a favor y en contra" },
        { id: "protocolo", label: "Protocolo de soporte" },
      ]} />

      <h2 id="hashimoto">Qué es Hashimoto</h2>
      <p>Es una enfermedad autoinmune donde el sistema inmunológico ataca por error a la tiroides. La tiroides, dañada lentamente, produce menos hormona → hipotiroidismo. Síntomas típicos: fatiga, frío, piel seca, caída de cabello, peso resistente, depresión leve, niebla mental.</p>

      <h2 id="gsh-tiroides">Glutatión y tiroides</h2>
      <p>La glándula tiroides produce H2O2 (peróxido de hidrógeno) como subproducto de la síntesis de hormona tiroidea. Necesita glutatión para neutralizarlo. Cuando el glutatión baja, el peróxido oxida tejido tiroideo → fuga de proteínas → el sistema inmune las identifica como "extrañas" → ataque autoinmune.</p>
      <p>Múltiples estudios muestran <strong>glutatión bajo en pacientes con Hashimoto</strong>, y mejoras clínicas con suplementación de precursores.</p>

      <h2 id="selenio">Selenio: el mineral indispensable</h2>
      <p>El selenio es cofactor de la enzima glutatión peroxidasa, que protege la tiroides directamente. Dosis útiles: 100-200 mcg/día. Fuente natural: nuez de Brasil (2-3 por día).</p>

      <Callout>
        Un meta-análisis en <em>Thyroid Research</em> mostró que selenio 200 mcg/día reduce significativamente los anticuerpos anti-TPO en Hashimoto.
      </Callout>

      <h2 id="alimentos">Alimentos a favor y en contra</h2>
      <p><strong>A favor:</strong> mariscos, nuez de Brasil, semillas de calabaza, huevo, pescado azul, kéfir, verduras crucíferas <em>cocidas</em> (crudas inhiben tiroides en exceso).</p>
      <p><strong>En contra:</strong> gluten (en Hashimoto, evidencia sugiere eliminar), ultraprocesados, soya en exceso, alcohol.</p>

      <h2 id="protocolo">Protocolo de soporte</h2>
      <ol>
        <li><strong>Mantén la levotiroxina o medicamento prescrito.</strong> No suspendas.</li>
        <li><Link to="/immunocal">Immunocal</Link> 1 sobre/día (precursor de glutatión).</li>
        <li>Selenio 150-200 mcg/día (1 nuez de Brasil aporta ~70 mcg).</li>
        <li>Zinc 15-25 mg/día (mineral cofactor).</li>
        <li>Vitamina D3 a 50-80 ng/ml (Hashimoto correlaciona con deficiencia).</li>
        <li>Prueba 8 semanas sin gluten → reevalúa síntomas.</li>
        <li>Manejo de estrés (cortisol crónico empeora tiroides).</li>
      </ol>

      <Esperanza />
    </>
  ),

  /* 18. Gastritis */
  "gastritis-cronica-y-glutation": (
    <>
      <p>La gastritis crónica es una de las quejas más comunes que escucho en mensajes. Personas que llevan años con omeprazol, dieta blanda, episodios recurrentes de ardor, distensión, náusea. La medicina convencional ataca el síntoma (acidez) pero rara vez resuelve la raíz: la <strong>mucosa gástrica está inflamada y oxidada</strong>.</p>

      <Disclaimer />

      <TOC items={[
        { id: "que-es", label: "Qué es la gastritis crónica" },
        { id: "h-pylori", label: "El factor H. pylori" },
        { id: "gsh-mucosa", label: "Glutatión y mucosa gástrica" },
        { id: "alimentos", label: "Alimentos sanadores y enemigos" },
        { id: "protocolo", label: "Protocolo de 90 días" },
      ]} />

      <h2 id="que-es">Qué es la gastritis crónica</h2>
      <p>Es la inflamación sostenida de la mucosa que recubre el estómago. Causada por: estrés crónico, alcohol, AINEs, café excesivo, infección por <em>Helicobacter pylori</em>, alimentos pro-inflamatorios.</p>

      <h2 id="h-pylori">El factor H. pylori</h2>
      <p>50-70 % de los mexicanos tiene H. pylori. No todos desarrollan gastritis, pero los que sí, viven en estado inflamatorio gástrico crónico. La bacteria activa estrés oxidativo extremo en la mucosa. <strong>Pídete prueba de aliento o antígeno en heces</strong> si tienes gastritis persistente.</p>

      <h2 id="gsh-mucosa">Glutatión y mucosa gástrica</h2>
      <p>La mucosa gástrica tiene una de las concentraciones más altas de glutatión del cuerpo: lo necesita para protegerse del propio ácido y de toxinas. En gastritis crónica, el glutatión local cae dramáticamente. Restaurarlo permite la reparación.</p>

      <Callout>
        Un estudio en <em>World Journal of Gastroenterology</em> mostró que pacientes con gastritis crónica que suplementaron con precursores de glutatión + tratamiento estándar tuvieron mejor recuperación de la mucosa que los del grupo control.
      </Callout>

      <h2 id="alimentos">Alimentos sanadores y enemigos</h2>
      <p><strong>Sanadores:</strong> caldo de hueso, plátano maduro, papaya, manzanilla, regaliz desglicirrizado (DGL), aloe vera (jugo puro), yogur natural sin azúcar, jengibre fresco.</p>
      <p><strong>Enemigos:</strong> café en ayunas, alcohol, chile en exceso, frituras, ultraprocesados, AINEs (ibuprofeno), tabaco.</p>

      <h2 id="protocolo">Protocolo de 90 días</h2>
      <ol>
        <li>Evalúa con tu médica si necesitas erradicar H. pylori (tratamiento de 14 días).</li>
        <li><Link to="/immunocal">Immunocal</Link> 1 sobre/día en agua fría (NO con jugo cítrico).</li>
        <li>DGL (regaliz desglicirrizado) 30 min antes de comer.</li>
        <li>Manzanilla + jengibre tras comidas.</li>
        <li>Comidas pequeñas y frecuentes, masticadas bien.</li>
        <li>Cero alcohol, café moderado y nunca en ayunas.</li>
        <li>Manejo de estrés diario (clave — el cortisol acidifica).</li>
      </ol>

      <Esperanza />
    </>
  ),

  /* 19. Colitis */
  "colitis-disbiosis-intestinal": (
    <>
      <p>La colitis no es "comer algo que te cayó mal una vez". Es inflamación sostenida del colon que se manifiesta con dolor, gases, distensión, episodios alternados de diarrea y estreñimiento. Detrás hay siempre dos protagonistas: <strong>inflamación y disbiosis</strong> (desequilibrio de la microbiota).</p>

      <Disclaimer />

      <TOC items={[
        { id: "tipos", label: "Tipos de colitis" },
        { id: "microbiota", label: "Tu microbiota: el otro órgano" },
        { id: "gsh-intestino", label: "Glutatión e intestino" },
        { id: "protocolo", label: "Protocolo 5R de medicina funcional" },
      ]} />

      <h2 id="tipos">Tipos de colitis</h2>
      <ul>
        <li><strong>SII (colitis nerviosa / irritable):</strong> el más común, ligado a estrés.</li>
        <li><strong>Colitis ulcerosa</strong> y <strong>Crohn:</strong> autoinmunes, requieren tratamiento médico estricto.</li>
        <li><strong>Colitis infecciosa:</strong> bacterias, parásitos.</li>
      </ul>

      <h2 id="microbiota">Tu microbiota: el otro órgano</h2>
      <p>Tienes 30-40 trillones de microorganismos en tu intestino. Cuando están equilibrados, producen butirato (combustible del colon), modulan inflamación, hacen vitaminas. Cuando se desequilibran (disbiosis): inflamación, permeabilidad ("intestino permeable"), síntomas sistémicos.</p>

      <h2 id="gsh-intestino">Glutatión e intestino</h2>
      <p>El intestino tiene glutatión específico para protegerse de toxinas, alimentos oxidados y subproductos bacterianos. En colitis, ese glutatión local cae. Restaurarlo permite que la barrera intestinal se repare.</p>

      <h2 id="protocolo">Protocolo 5R de medicina funcional</h2>
      <ol>
        <li><strong>Remover:</strong> alimentos disparadores (gluten, lácteos, ultraprocesados, alcohol) por 30 días.</li>
        <li><strong>Reemplazar:</strong> enzimas digestivas si las necesitas.</li>
        <li><strong>Reinocular:</strong> probióticos de calidad (Saccharomyces boulardii útil en colitis).</li>
        <li><strong>Reparar:</strong> L-glutamina, zinc-carnosina, omega-3, <Link to="/immunocal">Immunocal</Link>.</li>
        <li><strong>Reequilibrar:</strong> manejo de estrés diario, sueño consistente.</li>
      </ol>

      <Esperanza />
    </>
  ),
};

export default ARTICLE_CONTENT_5;
